import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { authService } from '../services/auth';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { LoginModal } from '../components/LoginModal';
import { 
  getUsageSummary, 
  getUsageHistory, 
  UsageSummary, 
  UsageEntry,
  formatCost,
  getModelDisplayName
} from '../services/credits';
import './Credits.css';

const tierColors: Record<string, string> = {
  free: '#10b981',
  pro: '#6366f1',
  enterprise: '#f59e0b',
  anonymous: '#737373',
};

export const Credits = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [usageHistory, setUsageHistory] = useState<UsageEntry[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange((currentUser) => {
      setUser(currentUser);
      setIsLoading(false);
      
      if (!currentUser) {
        navigate('/');
        return;
      }
      
      loadUsageData(currentUser.id);
    });
    return () => unsubscribe();
  }, [navigate]);

  const loadUsageData = async (userId: string) => {
    setDataLoading(true);
    try {
      const [summary, history] = await Promise.all([
        getUsageSummary(userId),
        getUsageHistory(userId, 50)
      ]);
      
      if (summary) {
        setUsageSummary(summary);
      }
      setUsageHistory(history);
    } catch (error) {
      console.error('Error loading usage data:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await authService.signOut();
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getRequestTypeBadge = (type: string) => {
    const types: Record<string, { label: string; color: string }> = {
      'simple_generation': { label: 'Simple', color: '#10b981' },
      'complex_generation': { label: 'Complex', color: '#f59e0b' },
      'stl_export': { label: 'Export', color: '#6366f1' },
      'step_export': { label: 'Export', color: '#6366f1' },
      'rendering': { label: 'Render', color: '#ec4899' },
    };
    return types[type] || { label: type, color: '#737373' };
  };

  if (isLoading) {
    return null;
  }

  if (!user) {
    return (
      <>
        <Header
          user={user}
          onLoginClick={() => setIsLoginModalOpen(true)}
          onSignOut={handleSignOut}
        />
        <LoginModal
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={() => {
            setIsLoginModalOpen(false);
          }}
        />
      </>
    );
  }

  return (
    <>
      <Header
        user={user}
        onLoginClick={() => setIsLoginModalOpen(true)}
        onSignOut={handleSignOut}
      />
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={() => {
          setIsLoginModalOpen(false);
        }}
      />

      <main className="page-main">
        {/* Header Section */}
        <section className="credits-header-section">
          <div className="credits-header-container">
            <div className="credits-header-content">
              <div className="page-badge">Dashboard</div>
              <h1>Credits & Usage</h1>
              <p>Track your usage and manage your credits</p>
            </div>
            {usageSummary && usageSummary.tier !== 'enterprise' && (
              <Link to="/pricing" className="btn-primary">
                {usageSummary.tier === 'free' ? 'Upgrade to Pro' : 'Manage Plan'}
              </Link>
            )}
          </div>
        </section>

        {/* Loading State */}
        {dataLoading && (
          <section className="credits-loading-section">
            <div className="credits-loading">
              <div className="loading-spinner-new"></div>
              <p>Loading usage data...</p>
            </div>
          </section>
        )}

        {/* Summary Cards */}
        {!dataLoading && usageSummary && (
          <section className="credits-summary-section">
            <div className="credits-summary-container">
              <div className="credits-summary-grid">
                {/* Plan Card */}
                <div className="credits-card plan-card-new">
                  <div className="plan-info-new">
                    <span 
                      className="plan-badge-new" 
                      style={{ background: tierColors[usageSummary.tier] }}
                    >
                      {usageSummary.tier_name}
                    </span>
                    <h3>Current Plan</h3>
                  </div>
                  <div className="plan-allowance-new">
                    <span className="allowance-amount">${usageSummary.monthly_allowance.toFixed(2)}</span>
                    <span className="allowance-label">monthly allowance</span>
                  </div>
                </div>

                {/* Usage Card */}
                <div className="credits-card usage-card-new">
                  <div className="usage-visual-new">
                    <div className="usage-ring-new">
                      <svg viewBox="0 0 100 100">
                        <circle
                          className="usage-ring-bg-new"
                          cx="50"
                          cy="50"
                          r="45"
                          strokeWidth="8"
                          fill="none"
                        />
                        <circle
                          className="usage-ring-progress-new"
                          cx="50"
                          cy="50"
                          r="45"
                          strokeWidth="8"
                          fill="none"
                          strokeDasharray={`${usageSummary.usage_percentage * 2.83} 283`}
                          style={{ stroke: tierColors[usageSummary.tier] }}
                        />
                      </svg>
                      <div className="usage-ring-text-new">
                        <span className="percentage-new">{usageSummary.usage_percentage.toFixed(0)}%</span>
                        <span className="label-new">used</span>
                      </div>
                    </div>
                  </div>
                  <div className="usage-details-new">
                    <div className="usage-row-new">
                      <span className="usage-label-new">Used</span>
                      <span className="usage-value-new">${usageSummary.credits_used.toFixed(2)}</span>
                    </div>
                    <div className="usage-row-new">
                      <span className="usage-label-new">Remaining</span>
                      <span className="usage-value-new remaining-new">${usageSummary.remaining_credits.toFixed(2)}</span>
                    </div>
                    {usageSummary.additional_credits > 0 && (
                      <div className="usage-row-new">
                        <span className="usage-label-new">Additional</span>
                        <span className="usage-value-new">${usageSummary.additional_credits.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions Card */}
                <div className="credits-card actions-card-new">
                  <h3>Quick Actions</h3>
                  <div className="action-buttons-new">
                    {usageSummary.tier === 'pro' && (
                      <button className="action-btn-new primary">
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
                        </svg>
                        Buy Credits
                      </button>
                    )}
                    <Link to="/viewer" className="action-btn-new secondary">
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                      Start Designing
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Usage History */}
        {!dataLoading && (
          <section className="credits-history-section">
            <div className="credits-history-container">
              <div className="history-header-new">
                <h2>Recent Usage</h2>
                <span className="history-count-new">{usageHistory.length} requests</span>
              </div>

              {usageHistory.length > 0 ? (
                <div className="history-table-wrapper">
                  <table className="history-table-new">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Model</th>
                        <th>Tokens</th>
                        <th>Cost</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usageHistory.map((entry) => {
                        const typeBadge = getRequestTypeBadge(entry.request_type);
                        return (
                          <tr key={entry.id}>
                            <td>
                              <span 
                                className="type-badge-new" 
                                style={{ background: typeBadge.color }}
                              >
                                {typeBadge.label}
                              </span>
                            </td>
                            <td className="model-cell">{getModelDisplayName(entry.model_used)}</td>
                            <td className="tokens-cell">
                              <span className="token-count-new">{entry.input_tokens + entry.output_tokens}</span>
                              <span className="token-breakdown-new">
                                ({entry.input_tokens} in / {entry.output_tokens} out)
                              </span>
                            </td>
                            <td className="cost-cell">{formatCost(entry.total_cost)}</td>
                            <td className="date-cell">{formatDate(entry.created_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-history-new">
                  <div className="empty-icon-new">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M3 3v18h18M7 16l4-4 4 4 5-6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>No usage yet</h3>
                  <p>Start designing to see your usage history here.</p>
                  <Link to="/viewer" className="btn-primary">Start Designing</Link>
                </div>
              )}
            </div>
          </section>
        )}

        {/* How Credits Work */}
        <section className="credits-info-section">
          <div className="credits-info-container">
            <div className="section-header-center">
              <span className="section-label">Understanding Credits</span>
              <h2>How Credits Work</h2>
            </div>
            <div className="credits-info-grid">
              <div className="credits-info-card">
                <div className="info-icon-new">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h4>AI Costs</h4>
                <p>Based on tokens used by the AI model. Different models have different rates.</p>
              </div>
              <div className="credits-info-card">
                <div className="info-icon-new">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h4>Infrastructure</h4>
                <p>Small cost for compute time and FreeCAD operations. Typically ~$0.001 per request.</p>
              </div>
              <div className="credits-info-card">
                <div className="info-icon-new">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h4>Monthly Reset</h4>
                <p>Credits reset at the start of each billing cycle. Unused credits do not roll over.</p>
              </div>
              <div className="credits-info-card">
                <div className="info-icon-new">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h4>Buy More</h4>
                <p>Pro users can purchase additional credits anytime. 1:1 dollar to credit ratio.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};
